import { isValidElement } from 'react';
import Button from '../Button.jsx';
import Badge from '../Badge.jsx';
import { headerCtaSize, normalizeHeaderSize } from './headerClasses.js';

/**
 * Maps header CTA config objects to {@link Button}.
 * Pass a React node only for non-button actions (e.g. `Dropdown`).
 *
 * @param {object|false|null|undefined} cta
 * @param {string} cta.label Button label (required for config objects).
 * @param {import('react').MouseEventHandler} [cta.onClick]
 * @param {import('lucide-react').LucideIcon} [cta.leftIcon]
 * @param {import('lucide-react').LucideIcon} [cta.rightIcon]
 * @param {'primary'|'classicPrimary'|'secondary'|'tertiary'|'ghost'|'error'|'danger'|'gradient'|'positive'|'purple'|'grey'} [cta.variant] Overrides slot default (`primary` / `secondary`).
 * @param {'sm'|'md'|'lg'} [cta.size] Overrides header size-derived default.
 * @param {boolean} [cta.disabled]
 * @param {boolean} [cta.loading]
 * @param {string} [cta.className]
 * @param {{ variant?: string, size?: string, defaults?: object }} [options]
 */
export function renderHeaderCta(cta, { variant = 'primary', size = 'md', defaults = {} }) {
  if (cta == null || cta === false) return null;
  if (isValidElement(cta)) return cta;

  if (typeof cta === 'object' && cta.label != null) {
    const {
      label,
      onClick,
      leftIcon,
      rightIcon,
      disabled,
      loading,
      className,
      variant: ctaVariant,
      size: ctaSize,
      ...rest
    } = cta;

    return (
      <Button
        type="button"
        variant={ctaVariant ?? variant}
        size={ctaSize ?? size}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        onClick={onClick}
        disabled={disabled}
        loading={loading}
        className={className}
        {...defaults}
        {...rest}
      >
        {label}
      </Button>
    );
  }

  return null;
}

/**
 * Maps header tag config objects to {@link Badge} (Figma Status Tag).
 * Pass a React node only when a custom chip is required.
 */
export function renderHeaderTag(tag, headerSize = 'L') {
  if (tag == null || tag === false) return null;
  if (isValidElement(tag)) return tag;

  const isCompress = normalizeHeaderSize(headerSize) === 'compress';
  const defaultBadgeSize = isCompress ? 'small' : 'medium';

  if (typeof tag === 'string') {
    return (
      <Badge variant="success" size={defaultBadgeSize}>
        {tag}
      </Badge>
    );
  }

  if (typeof tag === 'object' && tag.label != null) {
    return (
      <Badge
        variant={tag.variant ?? 'success'}
        size={tag.size ?? defaultBadgeSize}
        icon={tag.icon}
      >
        {tag.label}
      </Badge>
    );
  }

  return null;
}

function isLucideIcon(Component) {
  return typeof Component === 'function' || (typeof Component === 'object' && Component !== null);
}

export function renderHeaderIcon(icon, iconClassName = 'h-5 w-5') {
  if (icon == null || icon === false) return null;
  if (isValidElement(icon)) return icon;
  if (isLucideIcon(icon)) {
    const Icon = icon;
    return <Icon className={iconClassName} aria-hidden />;
  }
  return null;
}

export function resolveCtaSize(headerSize) {
  return headerCtaSize[headerSize] ?? headerCtaSize.L;
}
